import type { AccountStatusCode } from './accountStatus.interface';

/** Platform staff role, carried in the `pf` custom claim. Independent of organization roles. */
export const PLATFORM_ROLES = {
  ADMIN: 'admin',
  FINANCE: 'finance',
  SUPPORT: 'support',
  SALES: 'sales',
} as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[keyof typeof PLATFORM_ROLES];

/** Back-office mutations; each one maps to a `platform.<action>` API operation. */
export const PLATFORM_ACTIONS = {
  EXTEND_TRIAL: 'extendTrial',
  CHANGE_PLAN: 'changePlan',
  MARK_PAID: 'markPaid',
  SUSPEND: 'suspend',
  REACTIVATE: 'reactivate',
  CANCEL: 'cancel',
  LINK_ORGANIZATION: 'linkOrganizationToCorporation',
  CORPORATION_UPSERT: 'corporationUpsert',
} as const;

export type PlatformAction = (typeof PLATFORM_ACTIONS)[keyof typeof PLATFORM_ACTIONS];

export const ACCOUNT_TYPES = {
  CORPORATION: 'corporation',
  ORGANIZATION: 'organization',
} as const;

export type AccountType = (typeof ACCOUNT_TYPES)[keyof typeof ACCOUNT_TYPES];

export const ACCOUNT_PRICING_MODELS = {
  PER_SEAT: 'per_seat',
  TIER: 'tier',
  CUSTOM: 'custom',
} as const;

export type AccountPricingModel =
  (typeof ACCOUNT_PRICING_MODELS)[keyof typeof ACCOUNT_PRICING_MODELS];

export interface AccountPlan {
  code: string;
  pricingModel: AccountPricingModel;
  unitPrice?: number;
  currency: string;
  seatLimit?: number;
}

export interface CorporationFields {
  name: string;
  tradeName?: string;
  rut?: string;
  address?: string;
  serviceType?: string;
  auditUrl?: string;
  contractExpiryDate?: string;
  isActive?: boolean;
}

// ---------------------------------------------------------------------------------------------
// Transport (API) shapes. Timestamps arrive serialized (`{ _seconds, _nanoseconds }`).
// ---------------------------------------------------------------------------------------------

export interface PlatformAccountDTO {
  id: string;
  type: AccountType;
  corporationId?: string;
  organizationIds?: string[];
  status: AccountStatusCode;
  trialEndsAt?: unknown;
  graceEndsAt?: unknown;
  currentPeriodEnd?: unknown;
  plan?: AccountPlan;
  notes?: string;
  salesOwner?: string;
  mergedIntoAccountId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface PlatformAccountListItemDTO {
  account: PlatformAccountDTO;
  corporationName?: string;
  organizations: { id: string; name: string }[];
}

export interface PlatformOrganizationAdminDTO {
  id: string;
  name: string;
  email: string;
  phone?: string;
  access: string;
}

export interface PlatformOrganizationDetailDTO {
  id: string;
  name: string;
  email?: string;
  createdAt?: unknown;
  users: { total: number; active: number };
  admins: PlatformOrganizationAdminDTO[];
}

export interface PlatformCorporationDTO extends CorporationFields {
  id: string;
}

export interface PlatformAccountDetailDTO {
  account: PlatformAccountDTO;
  corporation?: PlatformCorporationDTO;
  organizations: PlatformOrganizationDetailDTO[];
}

export interface PlatformAuditEntryDTO {
  createdAt: number;
  changeCategory: string;
  description: string;
  operation?: string;
  userId?: string;
  user?: { name?: string; email?: string };
  extraKeys?: Record<string, string>;
  changedFields?: string[];
  changes?: string;
}

export interface PlatformAuditPageDTO {
  total: number;
  entries: PlatformAuditEntryDTO[];
}

export interface AccountListParams {
  status?: AccountStatusCode;
  search?: string;
}

export interface AccountActionParams {
  accountId: string;
  reason: string;
  /** Epoch millis; the API accepts millis, ISO strings or Timestamps. */
  trialEndsAt?: number;
  currentPeriodEnd?: number;
  plan?: AccountPlan;
}

export interface LinkOrganizationParams {
  organizationId: string;
  corporationId: string;
  reason: string;
}

export interface CorporationUpsertParams {
  reason: string;
  corporation: CorporationFields & { id?: string };
}

// ---------------------------------------------------------------------------------------------
// Domain shapes used by the back office screens.
// ---------------------------------------------------------------------------------------------

export interface PlatformAccount {
  id: string;
  type: AccountType;
  corporationId: string | null;
  organizationIds: string[];
  status: AccountStatusCode;
  trialEndsAt: Date | null;
  graceEndsAt: Date | null;
  currentPeriodEnd: Date | null;
  plan: AccountPlan | null;
  notes: string | null;
  salesOwner: string | null;
  mergedIntoAccountId: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface PlatformAccountListItem {
  /** The account id, at the top level so tables can key rows by it. */
  id: string;
  account: PlatformAccount;
  /** Corporation name, or the organization name for a single-organization account. */
  displayName: string;
  organizations: { id: string; name: string }[];
}

export interface PlatformOrganizationDetail {
  id: string;
  name: string;
  email: string | null;
  createdAt: Date | null;
  users: { total: number; active: number };
  admins: PlatformOrganizationAdminDTO[];
}

export interface PlatformCorporation extends CorporationFields {
  id: string;
}

export interface PlatformAccountDetail {
  account: PlatformAccount;
  corporation: PlatformCorporation | null;
  organizations: PlatformOrganizationDetail[];
}

/**
 * `staff`: a back-office action (`code` is the action, `reason` what the staff member wrote).
 * `lifecycle`: the daily job moved the account (`code` is the lifecycle reason, e.g. `trial_ended`).
 */
export interface PlatformAuditEntry {
  id: string;
  createdAt: Date | null;
  kind: 'staff' | 'lifecycle';
  code: string;
  reason: string | null;
  platformRole: string | null;
  actor: string | null;
  description: string;
}

export interface PlatformAuditPage {
  total: number;
  entries: PlatformAuditEntry[];
}

/** What a staff member submits from an action dialog. Dates are domain `Date`s. */
export type AccountActionInput =
  | {
      action: typeof PLATFORM_ACTIONS.EXTEND_TRIAL;
      accountId: string;
      reason: string;
      trialEndsAt: Date;
    }
  | {
      action: typeof PLATFORM_ACTIONS.CHANGE_PLAN;
      accountId: string;
      reason: string;
      plan: AccountPlan;
    }
  | {
      /** Reactivating needs a paid-through date too: the daily job would undo it otherwise. */
      action: typeof PLATFORM_ACTIONS.MARK_PAID | typeof PLATFORM_ACTIONS.REACTIVATE;
      accountId: string;
      reason: string;
      currentPeriodEnd: Date;
    }
  | {
      action: typeof PLATFORM_ACTIONS.SUSPEND | typeof PLATFORM_ACTIONS.CANCEL;
      accountId: string;
      reason: string;
    };
